import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const boldImpact: TemplateRegistration = {
  meta: {
    id: 'bold-impact',
    name: 'Bold Impact',
    category: 'Modern',
    description: 'High-contrast dramatic design with sharp edges and powerful typography',
    previewImage: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Anton', 'Inter']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-dark">
        <div class="container">
          <div class="about-card">
            <div class="accent-line"></div>
            <h2 class="section-title">About Us</h2>
            <p class="about-text">${safeAbout}</p>
            <div class="about-stats">
              ${rating ? `<div class="stat-item"><span class="stat-value">${rating.toFixed(1)}</span><span class="stat-label">Rating</span></div>` : ''}
              ${reviewCount ? `<div class="stat-item"><span class="stat-value">${reviewCount}+</span><span class="stat-label">Reviews</span></div>` : ''}
              <div class="stat-item"><span class="stat-value">${data.services.length}</span><span class="stat-label">Services</span></div>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceRows = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-row">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <div class="service-text">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-darker">
        <div class="container">
          <h2 class="section-title">What We Do</h2>
          <div class="services-list">${serviceRows}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section section-dark">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const featured = data.testimonials[0];
      const rest = data.testimonials.slice(1);
      const fName = sanitizeText(featured.name);
      const fContent = sanitizeText(featured.content);
      const fRole = featured.role ? sanitizeText(featured.role) : '';
      const restCards = rest.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-darker">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-featured">
            <div class="testimonial-featured-card">
              <div class="testimonial-body">
                <p>&ldquo;${fContent}&rdquo;</p>
              </div>
              <div class="testimonial-author">
                <strong>${fName}</strong>
                ${fRole ? `<span>${fRole}</span>` : ''}
                ${featured.rating ? `<div class="testimonial-stars">${renderStars(featured.rating)}</div>` : ''}
              </div>
            </div>
          </div>
          ${rest.length > 0 ? `<div class="testimonials-rest">${restCards}</div>` : ''}
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-dark">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-dark">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-centered">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #0c0a09; --bg-alt: #1c1917; --accent: #eab308;
      --text: #fafaf9; --text-muted: #a8a29e;
      --font-heading: 'Anton', sans-serif; --font-body: 'Inter', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(12,10,9,.92); backdrop-filter: blur(8px); border-bottom: 2px solid var(--accent); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1200px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.5rem; color: var(--text); text-transform: uppercase; letter-spacing: 2px; }
    .nav-links { display: flex; gap: 28px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 500; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 28px; height: 3px; background: var(--accent); margin: 5px 0; transition: .3s; }

    /* HERO - image-first with overlay at bottom */
    .hero { position: relative; min-height: 100vh; display: flex; flex-direction: column; overflow: hidden; }
    .hero-image-area { position: relative; flex: 1; min-height: 70vh; overflow: hidden; }
    .hero-image-area img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .hero-image-area .hero-no-img { width: 100%; height: 100%; background: linear-gradient(135deg, #292524, var(--bg-alt)); }
    .hero-bottom { position: absolute; bottom: 0; left: 0; right: 0; z-index: 2; background: linear-gradient(to top, var(--bg) 0%, rgba(12,10,9,.85) 60%, transparent 100%); padding: 80px 24px 48px; }
    .hero-bottom-inner { max-width: 1200px; margin: 0 auto; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(3rem, 8vw, 6rem); line-height: 1.05; text-transform: uppercase; margin-bottom: 16px; }
    .hero h1 .accent-underline { display: inline; background-image: linear-gradient(var(--accent), var(--accent)); background-size: 100% 6px; background-repeat: no-repeat; background-position: bottom; padding-bottom: 4px; }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.25rem); color: var(--text-muted); max-width: 600px; margin-bottom: 32px; }
    .hero-cta { display: inline-block; padding: 16px 40px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: 1.1rem; text-transform: uppercase; letter-spacing: 2px; border: none; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(234,179,8,.3); opacity: 1; color: var(--bg); }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-dark { background: var(--bg); }
    .section-darker { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(2rem, 5vw, 3rem); text-transform: uppercase; margin-bottom: 48px; }
    .accent-line { width: 80px; height: 4px; background: var(--accent); margin-bottom: 24px; }

    /* ABOUT - card with inline stat highlights */
    .about-card { background: var(--bg-alt); border: 2px solid #292524; padding: 48px 40px; max-width: 900px; }
    .about-card .about-stats { display: flex; gap: 32px; margin-top: 24px; flex-wrap: wrap; }
    .about-card .stat-item { text-align: center; }
    .about-card .stat-value { display: block; font-family: var(--font-heading); font-size: 2rem; color: var(--accent); }
    .about-card .stat-label { font-size: .8rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 1px; }
    .about-text { font-size: 1.1rem; color: var(--text-muted); max-width: 800px; line-height: 1.8; }

    /* SERVICES - alternating rows */
    .services-list { display: flex; flex-direction: column; gap: 0; }
    .service-row { display: flex; align-items: center; gap: 32px; padding: 40px 32px; border-bottom: 2px solid #292524; transition: background .3s; }
    .service-row:hover { background: rgba(234,179,8,.04); }
    .service-row:nth-child(even) { flex-direction: row-reverse; }
    .service-row .service-icon { color: var(--accent); flex-shrink: 0; }
    .icon-char { font-size: 28px; line-height: 1; }
    .service-row .service-text { flex: 1; }
    .service-row h3 { font-family: var(--font-heading); font-size: 1.3rem; text-transform: uppercase; margin-bottom: 8px; }
    .service-row p { color: var(--text-muted); font-size: .95rem; }
    .service-price { display: inline-block; margin-top: 12px; color: var(--accent); font-weight: 700; font-size: 1.1rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .gallery-item img { width: 100%; height: 240px; object-fit: cover; display: block; filter: grayscale(.2); transition: filter .3s; }
    .gallery-item img:hover { filter: grayscale(0); }

    /* TESTIMONIALS - single featured quote + smaller below */
    .testimonials-featured { margin-bottom: 40px; }
    .testimonial-featured-card { border-left: 6px solid var(--accent); padding: 48px 40px; background: var(--bg); }
    .testimonial-featured-card .testimonial-body p { font-size: 1.35rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 20px; }
    .testimonial-featured-card .testimonial-author strong { display: block; color: var(--text); font-size: 1.1rem; }
    .testimonial-featured-card .testimonial-author span { color: var(--text-muted); font-size: .9rem; }
    .testimonials-rest { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px; }
    .testimonial-card { border-left: 4px solid var(--accent); padding: 24px; background: var(--bg); }
    .testimonial-body p { font-size: .95rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: .9rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .8rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #57534e; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; }
    .faq-item { border-bottom: 2px solid #292524; padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.2rem; text-transform: uppercase; margin-bottom: 12px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 2px solid #292524; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; text-transform: uppercase; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.5rem; color: var(--accent); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid #292524; color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--accent); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--bg); font-size: 1.2rem; }
    .review-text { color: var(--bg); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: #292524; padding: 20px 0; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--accent); color: var(--bg); font-family: var(--font-heading); padding: 8px 20px; font-size: 1.1rem; text-transform: uppercase; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - centered card (stacked info + form) */
    .contact-centered { max-width: 600px; margin: 0 auto; background: var(--bg); border: 2px solid #292524; padding: 48px 40px; }
    .contact-info { display: flex; flex-direction: column; gap: 16px; margin-bottom: 32px; text-align: center; align-items: center; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: #292524; border: 2px solid #44403c; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 16px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: 1rem; text-transform: uppercase; letter-spacing: 2px; border: none; cursor: pointer; transition: transform .2s; }
    .contact-form button:hover { transform: translateY(-2px); }

    /* FOOTER */
    .footer { background: var(--bg-alt); border-top: 2px solid var(--accent); padding: 40px 0; text-align: center; }
    .footer p { color: var(--text-muted); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(12,10,9,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .service-row, .service-row:nth-child(even) { flex-direction: column; text-align: center; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-centered { padding: 32px 20px; }
      .testimonials-rest { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; gap: 8px; }
      .about-card .about-stats { justify-content: center; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-bottom { padding: 60px 16px 32px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-image-area">
      ${heroImage ? `<img src="${escapeAttr(heroImage)}" alt="${safeName}">` : `<div class="hero-no-img"></div>`}
    </div>
    <div class="hero-bottom">
      <div class="hero-bottom-inner">
        <h1><span class="accent-underline">${safeHeadline}</span></h1>
        <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
